import type { GridColumn, SizedGridColumn } from "@glideapps/glide-data-grid";
import { useMemo, useState } from "react";
import {
  useUpdateAiSpreadsheetSheet,
  type UpdateAiSpreadsheetSheetParams,
} from "@/payroll/ai-script/query";
import type { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";
import { pixelWidthToCharacterUnits } from "./adapter";
import { pixelHeightToPoints, type RowResizeTarget } from "./row-resize";

interface UseSheetLayoutParams {
  businessId: string | null;
  runId: string;
  sheet: Pick<IAiSpreadsheetSheet, "id" | "frozen_column"> | null;
  editable: boolean;
  columns: SizedGridColumn[] | undefined;
  onError: (title: string, error: unknown) => void;
}

export const useSheetLayout = ({
  businessId,
  runId,
  sheet,
  editable,
  columns,
  onError,
}: UseSheetLayoutParams) => {
  const { mutateAsync: updateSheetLayout } =
    useUpdateAiSpreadsheetSheet(businessId);
  const scope = JSON.stringify([businessId, runId, sheet?.id]);
  const [savingCounts, setSavingCounts] = useState<Record<string, number>>({});
  const [columnWidthOverrides, setColumnWidthOverrides] = useState<
    Record<string, Record<string, number>>
  >({});

  const resizedColumns = useMemo(
    () =>
      (columns ?? []).map((column) => {
        const width = columnWidthOverrides[scope]?.[String(column.id)];
        return width === undefined ? column : { ...column, width };
      }),
    [columns, columnWidthOverrides, scope],
  );

  // Each request retains its source sheet while the user switches tabs. Counts
  // allow rapid resizes without an earlier response clearing a later save.
  const saveLayout = (
    title: string,
    params: UpdateAiSpreadsheetSheetParams,
  ) => {
    const sheetId = params.sheetId;
    setSavingCounts((current) => ({
      ...current,
      [sheetId]: (current[sheetId] ?? 0) + 1,
    }));
    void updateSheetLayout(params)
      .catch((error: unknown) => onError(title, error))
      .finally(() => {
        setSavingCounts((current) => {
          const next = { ...current };
          const remaining = (next[sheetId] ?? 1) - 1;
          if (remaining > 0) next[sheetId] = remaining;
          else delete next[sheetId];
          return next;
        });
      });
  };

  const onColumnResize = (column: GridColumn, pixels: number) => {
    setColumnWidthOverrides((current) => ({
      ...current,
      [scope]: { ...current[scope], [String(column.id)]: pixels },
    }));
  };

  const onColumnResizeEnd = (
    _column: GridColumn,
    pixels: number,
    columnIndex: number,
  ) => {
    if (!sheet || !editable) return;
    saveLayout("Failed to save column width", {
      runId,
      sheetId: sheet.id,
      columnWidths: {
        [String(columnIndex + 1)]: pixelWidthToCharacterUnits(pixels),
      },
    });
  };

  const onRowResizeEnd = ({ sheetId, row, pixels }: RowResizeTarget) => {
    if (!editable) return;
    saveLayout("Failed to save row height", {
      runId,
      sheetId,
      rowHeights: { [String(row)]: pixelHeightToPoints(pixels) },
    });
  };

  const onFrozenColumnChange = (frozenColumn: number | null) => {
    if (!sheet || !editable || sheet.frozen_column === frozenColumn) return;
    saveLayout("Failed to save frozen columns", {
      runId,
      sheetId: sheet.id,
      frozenColumn,
    });
  };

  return {
    scope,
    columns: resizedColumns,
    isSaving: (savingCounts[sheet?.id ?? ""] ?? 0) > 0,
    onColumnResize,
    onColumnResizeEnd,
    onRowResizeEnd,
    onFrozenColumnChange,
  };
};
